import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { finalize, tap, catchError } from 'rxjs/operators';
import { LoadingService } from './loading.service';
import { throwError } from 'rxjs';

@Injectable()
export class NetworkInterceptor implements HttpInterceptor {

  totalRequests = 0;
  requestsCompleted = 0;

  constructor(private loader: LoadingService) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {

    this.loader.show();
    this.totalRequests++;

    // Remove any CORS headers that might have been added (these should never be in requests)
    let headers = request.headers;
    if (headers.has('Access-Control-Allow-Origin')) {
      headers = headers.delete('Access-Control-Allow-Origin');
    }
    if (headers.has('Access-Control-Allow-Methods')) {
      headers = headers.delete('Access-Control-Allow-Methods');
    }
    if (headers.has('Access-Control-Allow-Headers')) {
      headers = headers.delete('Access-Control-Allow-Headers');
    }
    
    // Clone request with cleaned headers if any were removed
    if (headers !== request.headers) {
      request = request.clone({ headers });
      console.log('⚠️ Removed client-side CORS headers from request');
    }

    // Log request details
    console.log('🚀 HTTP Request:', {
      method: request.method,
      url: request.url,
      urlWithParams: request.urlWithParams,
      headers: this.getHeadersAsObject(request.headers),
      body: request.body,
      withCredentials: request.withCredentials,
      responseType: request.responseType
    });

    return next.handle(request).pipe(
      tap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          console.log('✅ HTTP Response:', {
            url: request.url,
            status: event.status,
            statusText: event.statusText,
            headers: this.getHeadersAsObject(event.headers)
          });
        }
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('❌ HTTP Error:', {
          url: request.url,
          status: error.status,
          statusText: error.statusText,
          message: error.message,
          headers: this.getHeadersAsObject(error.headers),
          error: error.error
        });

        // Special handling for 403 Forbidden
        if (error.status === 403) {
          console.error('🚫 403 FORBIDDEN ERROR DETAILS:', {
            url: request.url,
            requestHeaders: this.getHeadersAsObject(request.headers),
            responseHeaders: this.getHeadersAsObject(error.headers),
            errorBody: error.error,
            possibleWAFRules: this.analyzeWAFError(error)
          });
        }

        return throwError(() => error);
      }),
      finalize(() => {
        this.requestsCompleted++;
        if (this.requestsCompleted === this.totalRequests) {
          this.loader.hide();
          this.totalRequests = 0; 
          this.requestsCompleted = 0;
        }
      })
    );
  }

  private getHeadersAsObject(headers: any): any {
    const headersObj: any = {};
    if (headers && headers.keys) {
      headers.keys().forEach((key: string) => {
        headersObj[key] = headers.get(key);
      });
    }
    return headersObj;
  }

  private analyzeWAFError(error: HttpErrorResponse): string[] {
    const suggestions: string[] = [];
    const errorText = JSON.stringify(error.error || '').toLowerCase();

    if (errorText.includes('sql') || errorText.includes('injection')) {
      suggestions.push('Rule 942100/942200 - SQL Injection Detection');
    }
    if (errorText.includes('xss') || errorText.includes('script')) {
      suggestions.push('Rule 941100 - XSS Attack Detection');
    }
    if (errorText.includes('path') || errorText.includes('traversal')) {
      suggestions.push('Rule 930100 - Path Traversal Attack');
    }
    if (errorText.includes('cors') || errorText.includes('origin')) {
      suggestions.push('CORS/Origin header validation');
    }
    if (!error.headers || !error.headers.get('access-control-allow-origin')) {
      suggestions.push('Missing CORS headers - check WAF CORS configuration');
    }

    return suggestions.length > 0 ? suggestions : ['Generic WAF block - check Azure WAF logs for specific rule ID'];
  }
}
